import React, { Component } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import _ from 'lodash'

import { TABLE_COLUMN_WIDTH } from '../../constants'

import { getCSSYearClass } from './helpers'

import ColumnHeader from './components/ColumnHeader'
import ColumnBody from './components/ColumnBody'

import './style.scss'

export default class DataColumns extends Component {
    static propTypes = {
        activeRowIndex: PropTypes.string,
        changeYearBounds: PropTypes.func.isRequired,
        compareYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        data: PropTypes.array,
        decimalPlaces: PropTypes.number,
        handleCompareYearChange: PropTypes.func.isRequired,
        // standards: PropTypes.arrayOf(PropTypes.shape({
        //     standardType: PropTypes.string.isRequired,
        //     standardTitle: PropTypes.string.isRequied,
        // })),
        handleMouseLeaveRow: PropTypes.func.isRequired,
        handleRowHover: PropTypes.func.isRequired,
        handleSampleYearChange: PropTypes.func.isRequired,
        id: PropTypes.string.isRequired,
        isAdvanced: PropTypes.bool.isRequired,
        lowerBound: PropTypes.number,
        numberMagnitude: PropTypes.number,
        openRows: PropTypes.array,
        sampleYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        sectionsDefinition: PropTypes.arrayOf(
            PropTypes.shape({
                title: PropTypes.string.isRequired,
                rows: PropTypes.arrayOf(
                    PropTypes.shape({
                        prefix: PropTypes.string,
                        highlight: PropTypes.bool,
                        text: PropTypes.string.isRequired,
                        columnCode: PropTypes.string.isRequired,
                        indentationLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                        configuration: PropTypes.shape({
                            unit: PropTypes.string,
                            higherIsBetter: PropTypes.string,
                        }),
                    })
                ).isRequired,
            })
        ),
        setYearBounds: PropTypes.func.isRequired,
        setYears: PropTypes.func.isRequired,
        showUnitsAndCurr: PropTypes.bool,
        tableType: PropTypes.string.isRequired,
        translates: PropTypes.shape({
            asCompareYear: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            asSampleYear: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            dateFrom: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            dateTo: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            standard: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            sampleYear: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            compareYear: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            audit: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            consolidation: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            thousand: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            million: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            billion: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            trillion: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            CZK: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            EUR: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            yes: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            no: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            noData: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            subjects: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
        }).isRequired,
        upperBound: PropTypes.number,
    }

    constructor(props) {
        super(props)

        this.columnsContainer = React.createRef()
    }

    componentDidMount() {
        this.handleResize()
        window.addEventListener('resize', this.handleResize)
    }

    componentDidUpdate(oldProps) {
        const { id, data } = this.props

        if (id !== oldProps.id) this.handleResize()
        if (!_.isEqual(data, oldProps.data)) this.handleResize()
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize)
    }

    handleResize = () => {
        const { setYearBounds, tableType, id } = this.props
        const maximumColumns = _.parseInt((this.columnsContainer.current.offsetWidth - 36) / TABLE_COLUMN_WIDTH)

        setYearBounds(id, tableType, 0, maximumColumns)
    }

    handleShiftYearsLeft = () => {
        const { id, data, changeYearBounds, tableType } = this.props

        changeYearBounds(id, tableType, true, data.length)
    }

    handleShiftYearsRight = () => {
        const { id, data, changeYearBounds, tableType } = this.props

        changeYearBounds(id, tableType, false, data.length)
    }

    renderColumns = (data, sectionsDefinition) => {
        const {
            id,
            compareYear,
            sampleYear,
            lowerBound,
            showUnitsAndCurr,
            upperBound,
            activeRowIndex,
            handleMouseLeaveRow,
            numberMagnitude,
            openRows,
            isAdvanced,
            handleRowHover,
            handleCompareYearChange,
            handleSampleYearChange,
            setYears,
            translates,
            decimalPlaces,
            tableType,
        } = this.props

        const years = _(data).slice(lowerBound, upperBound).reverse().value()

        if (_.isEmpty(data)) {
            return (
                <div className={'no-data'}>
                    <div className="no-data-text">{translates.noData}</div>
                </div>
            )
        }

        return _.map(years, (column, index) => (
            <div
                key={`dc-${column.year || index}-column`}
                className={classnames('dc-column', getCSSYearClass(_.get(column, 'year'), sampleYear, compareYear))}
            >
                <ColumnHeader
                    id={id}
                    column={column}
                    data={data}
                    compareYear={compareYear}
                    sampleYear={sampleYear}
                    showUnitsAndCurr={showUnitsAndCurr}
                    handleCompareYearChange={handleCompareYearChange}
                    handleSampleYearChange={handleSampleYearChange}
                    numberMagnitude={numberMagnitude}
                    setYears={setYears}
                    tableType={tableType}
                    translates={translates}
                />
                <ColumnBody
                    id={id}
                    activeRowIndex={activeRowIndex}
                    column={column}
                    isAdvanced={isAdvanced}
                    data={data}
                    decimalPlaces={decimalPlaces}
                    numberMagnitude={numberMagnitude}
                    handleMouseLeaveRow={handleMouseLeaveRow}
                    handleRowHover={handleRowHover}
                    sectionsDefinition={sectionsDefinition}
                    openRows={openRows}
                />
            </div>
        ))
    }

    render() {
        const { data, sectionsDefinition, lowerBound, upperBound } = this.props

        return (
            <div className={'data-column-container'}>
                <div ref={this.columnsContainer} className={'data-columns'}>
                    {this.renderColumns(data, sectionsDefinition)}
                    <button
                        disabled={upperBound >= _.size(data)}
                        className={classnames('finance-column-btn prev', upperBound >= _.size(data) && 'disabled')}
                        onClick={() => this.handleShiftYearsLeft()}
                    />
                    <button
                        disabled={lowerBound <= 0}
                        className={classnames('finance-column-btn next', lowerBound <= 0 && 'disabled')}
                        onClick={() => this.handleShiftYearsRight()}
                    />
                </div>
            </div>
        )
    }
}
